import {
  assertChallengeResponse,
  normalizeSessionDataResponse,
} from '../auth-contracts/index.js';
import { inferProductFromHost } from '../config/domainMap.js';

export function createAuthClient(ajax) {
  if (!ajax || typeof ajax.get !== 'function' || typeof ajax.post !== 'function') {
    throw new Error('createAuthClient requer uma instância Ajax válida.');
  }

  const authServiceCategoryEnv = typeof process !== 'undefined' ? process.env.AUTH_SERVICE_CATEGORY : '';
  const authServiceDomainEnv = typeof process !== 'undefined' ? process.env.AUTH_SERVICE_DOMAIN : '';
  const authCategory = String(
    authServiceCategoryEnv
    || 'security'
  ).trim().toLowerCase();
  const authDomain = String(
    authServiceDomainEnv
    || 'identity'
  ).trim().toLowerCase();

  const call = ({ category, domain, module, method, body = {}, config = {} }) => ajax.post({
    product: 'auth',
    category,
    domain,
    module,
    method,
    body,
    config,
  });

  const callAuth = (method, body = {}, config = {}) => call({
    category: authCategory,
    domain: authDomain,
    module: 'auth',
    method,
    body,
    config,
  });

  const normalizePayloadAndConfig = (payloadOrConfig = {}, maybeConfig = {}) => {
    const configKeys = new Set(['loader', 'retries', 'retryDelay', 'credentials', 'forceRefresh']);
    const first = payloadOrConfig && typeof payloadOrConfig === 'object' && !Array.isArray(payloadOrConfig)
      ? payloadOrConfig
      : {};
    const second = maybeConfig && typeof maybeConfig === 'object' && !Array.isArray(maybeConfig)
      ? maybeConfig
      : {};

    const firstKeys = Object.keys(first);
    const looksLikeConfigOnly = firstKeys.length > 0 && firstKeys.every((key) => configKeys.has(key));
    if (looksLikeConfigOnly && Object.keys(second).length === 0) {
      return { payload: {}, config: first };
    }

    return { payload: first, config: second };
  };

  const callMfa = (method, body = {}, config = {}) => call({
    category: authCategory,
    domain: authDomain,
    module: 'mfa',
    method,
    body,
    config,
  });

  const callSignup = (method, body = {}, config = {}) => call({
    category: authCategory,
    domain: 'tenant',
    module: 'signup',
    method,
    body,
    config,
  });

  return {
    async challenge(email, config = {}) {
      const response = await callAuth('challenge', { query: { email } }, config);
      return assertChallengeResponse(response);
    },

    async getSessionData(config = {}) {
      const forceRefresh = config?.forceRefresh === true;
      const { forceRefresh: _forceRefresh, ...requestConfig } = (config && typeof config === 'object') ? config : {};
      const response = await ajax.get({
        product: 'auth',
        category: 'core',
        domain: 'core',
        module: 'master',
        method: 'getSessionData',
        query: forceRefresh ? { refresh: '1' } : {},
        config: requestConfig,
      });
      return normalizeSessionDataResponse(response);
    },

    async getMenu(config = {}) {
      // Catalog presentation (including icons) can change while a session is
      // active. Normal menu loads must not keep an older session snapshot.
      const forceRefresh = config?.forceRefresh !== false;
      const { forceRefresh: _forceRefresh, ...requestConfig } = (config && typeof config === 'object') ? config : {};
      const currentProduct = typeof window !== 'undefined'
        ? inferProductFromHost(window.location?.hostname || '')
        : '';
      return ajax.get({
        product: currentProduct || 'platform',
        category: 'core',
        domain: 'core',
        module: 'master',
        method: 'getMenu',
        query: forceRefresh ? { refresh: '1' } : {},
        config: requestConfig,
      });
    },

    async verifyPassword(payload, config = {}) {
      return callAuth('verifyPassword', { data: payload }, config);
    },

    async verifyCode(payload, config = {}) {
      return callAuth('verifyCode', { data: payload }, config);
    },

    async issueProductSessionGrant(payload = {}, config = {}) {
      return callAuth('issueProductSessionGrant', payload, config);
    },

    async consumeProductSessionGrant(payload = {}, config = {}) {
      return callAuth('consumeProductSessionGrant', payload, config);
    },

    async issueSsoGrant(payload = {}, config = {}) {
      return callAuth('issueProductSessionGrant', payload, config);
    },

    async consumeSsoGrant(payload = {}, config = {}) {
      return callAuth('consumeProductSessionGrant', payload, config);
    },

    async logoutCurrent(payloadOrConfig = {}, maybeConfig = {}) {
      const { payload, config } = normalizePayloadAndConfig(payloadOrConfig, maybeConfig);
      return callAuth('logoutCurrent', payload, config);
    },

    async revokeOtherSessions(config = {}) {
      return callAuth('revokeOtherSessions', {}, config);
    },

    async revokeSessionByEmail(email, config = {}) {
      return callAuth('revokeSession', { email }, config);
    },

    async chooseMfaMethod(payload, config = {}) {
      return callMfa('chooseMethod', { data: payload }, config);
    },

    async verifyPasswordResetCode(payload, config = {}) {
      return callAuth('verifyPasswordResetCode', payload, config);
    },

    async confirmPasswordReset(payload, config = {}) {
      return callAuth('confirmPasswordReset', payload, config);
    },

    async changePassword(payload, config = {}) {
      return callAuth('changePassword', payload, config);
    },

    async callAuthMethod(method, payload = {}, config = {}) {
      return callAuth(method, payload, config);
    },

    async getSignupCatalog(payload = {}, config = {}) {
      return callSignup('getCatalog', payload, config);
    },

    async saveSignup(payload = {}, config = {}) {
      return callSignup('save', payload, config);
    },
  };
}
